/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useQuery } from 'react-query'

import {
  listElasticsearchProjects,
  listObservabilityProjects,
  listSecurityProjects,
  getElasticsearchProjectLinkCandidates,
  getObservabilityProjectLinkCandidates,
  getSecurityProjectLinkCandidates,
} from '@modules/project-user-api/v1/callers'
import type { MultiErrorResponse } from '@modules/project-user-api/v1/types'
import type { QueryHook, QueryHookOptions, QueryFunctionReturnType } from '@modules/query/types'
import type { ProjectID } from '@modules/ui-types/projects'

import { makeQueryKey } from './helpers'

export const useListElasticsearchProjectsQuery: QueryHook<
  typeof listElasticsearchProjects,
  unknown,
  MultiErrorResponse
> = (options?: QueryHookOptions) =>
  useQuery(makeQueryKey('listElasticsearchProjects'), () => listElasticsearchProjects(), options)

export const useListObservabilityProjectsQuery: QueryHook<
  typeof listObservabilityProjects,
  unknown,
  MultiErrorResponse
> = (options?: QueryHookOptions) =>
  useQuery(makeQueryKey('listObservabilityProjects'), () => listObservabilityProjects(), options)

export const useListSecurityProjectsQuery: QueryHook<
  typeof listSecurityProjects,
  unknown,
  MultiErrorResponse
> = (options?: QueryHookOptions) =>
  useQuery(makeQueryKey('listSecurityProjects'), () => listSecurityProjects(), options)

export const useListElasticsearchProjectsWithArgsQuery = (
  options?: QueryHookOptions,
  args?: Exclude<Parameters<typeof listElasticsearchProjects>[0], undefined>['queryParameters'],
) =>
  useQuery<QueryFunctionReturnType<typeof listElasticsearchProjects>, MultiErrorResponse>(
    makeQueryKey('listElasticsearchProjects', args),
    () =>
      listElasticsearchProjects({
        queryParameters: args,
      }),
    options,
  )

export const useListObservabilityProjectsWithArgsQuery = (
  options?: QueryHookOptions,
  args?: Exclude<Parameters<typeof listObservabilityProjects>[0], undefined>['queryParameters'],
) =>
  useQuery<QueryFunctionReturnType<typeof listObservabilityProjects>, MultiErrorResponse>(
    makeQueryKey('listObservabilityProjects', args),
    () =>
      listObservabilityProjects({
        queryParameters: args,
      }),
    options,
  )

export const useListSecurityProjectsWithArgsQuery = (
  options?: QueryHookOptions,
  args?: Exclude<Parameters<typeof listSecurityProjects>[0], undefined>['queryParameters'],
) =>
  useQuery<QueryFunctionReturnType<typeof listSecurityProjects>, MultiErrorResponse>(
    makeQueryKey('listSecurityProjects', args),
    () =>
      listSecurityProjects({
        queryParameters: args,
      }),
    options,
  )

export const useGetElasticsearchLinkCandidatesQuery = (
  projectId: ProjectID,
  options?: QueryHookOptions,
) =>
  useQuery<
    QueryFunctionReturnType<typeof getElasticsearchProjectLinkCandidates>,
    MultiErrorResponse
  >(
    makeQueryKey('getElasticsearchProjectLinkCandidates', { id: projectId }),
    () =>
      getElasticsearchProjectLinkCandidates({
        pathParameters: { id: projectId },
      }),
    options,
  )

export const useGetObservabilityLinkCandidatesQuery = (
  projectId: ProjectID,
  options?: QueryHookOptions,
) =>
  useQuery<
    QueryFunctionReturnType<typeof getObservabilityProjectLinkCandidates>,
    MultiErrorResponse
  >(
    makeQueryKey('getObservabilityProjectLinkCandidates', { id: projectId }),
    () =>
      getObservabilityProjectLinkCandidates({
        pathParameters: { id: projectId },
      }),
    options,
  )

export const useGetSecurityLinkCandidatesQuery = (
  projectId: ProjectID,
  options?: QueryHookOptions,
) =>
  useQuery<QueryFunctionReturnType<typeof getSecurityProjectLinkCandidates>, MultiErrorResponse>(
    makeQueryKey('getSecurityProjectLinkCandidates', { id: projectId }),
    () =>
      getSecurityProjectLinkCandidates({
        pathParameters: { id: projectId },
      }),
    options,
  )
